import type { LoginProvider } from '@/enums/login';
import { ProfileSpecificLink } from '@/@types/graphql-types';
import { defineStore } from 'pinia';
import { useUserStore } from '@/stores/pinia/useUserStore';

export const EMAIL_HASH_KEY = 'hachee';

export const CONSENT_PARTNERS = {
	google: { name: 'Google', short: 'G', id: '1' },
	facebook: { name: 'Facebook', short: 'FB', id: '2' },
	twitter: { name: 'Twitter', short: 'TW', id: '4' },
	tiktok: { name: 'TikTok', short: 'TT', id: '6' },
	snapchat: { name: 'Snapchat', short: 'SC', id: '8' },
	allother: { name: 'All Other Providers', short: 'AO', id: '5' },
	emails: { name: 'Email', short: 'E', id: '7' },
	terms_and_conditions: { name: 'Terms and Conditions', short: 'TC', id: '9' },
} as const;

export type Consent = keyof typeof CONSENT_PARTNERS;

export type UpdateUserInfoPayload = {
	loginProvider: LoginProvider;
	name: string;
	email: string;
	emailVerified: boolean;
	photoURL: string;
};

export enum BuyBoxFilterOptions {
	ALL = 'all',
	SUBSCRIPTION = 'flatrate',
	FREE = 'free',
	FAST_TV = 'fast',
	BUY = 'buy',
	RENT = 'rent',
	CINEMA = 'cinema',
	PHYSICAL = 'physical',
}

export enum NewBuyBoxView {
	LIST = 'list',
	GRID = 'grid',
}

export interface UserInfo {
	email?: string;
	loginProvider?: LoginProvider;
	name?: string;
	emailVerified?: boolean;
	creationTime?: Date;
	photoURL?: string;
}

export enum TasteSurveyType {
	NONE = 'NONE',
	DIVERGING_TITLES = 'DIVERGING_TITLES',
	FINDER_GAME = 'FINDER_GAME',
	TV_APP_BLANK = 'TV_APP_BLANK',
	AUTO_DIGGER = 'AUTO_DIGGER',
	STATIC_DIGGER = 'STATIC_DIGGER',
	STATIC_MAPPINGS = 'STATIC_MAPPINGS',
	GLOBAL_CLICKED_CTA = 'GLOBAL_CLICKED_CTA',
}

export enum VisibleFlyoutType {
	HOME_INVITATION_BOX = 'homeInvitationBox',
	ONBOARDED_HOME = 'onboardedHome',
	FREEMIUM_BANNER = 'freemiumBanner',
}

export enum premiumPurchaseFlowState {
	AUTH_STARTED = 'AUTH_STARTED',
	AUTH_SUCCESS = 'AUTH_SUCCESS',
	CHECKOUT = 'CHECKOUT',
}

export interface UserSettings {
	hidden_timeline_specials: string[];
	jw_consents: Record<Consent, boolean | null>;
	providers: string[];
	uc_consents: { id: string; consent: boolean }[] | null;
}

export interface ProfileInfo {
	uuid: string | null;
	firstName: string | null;
	lastName: string | null;
	city: string | null;
	country: string | null;
	bio: string | null;
	avatarUrl: string | null;
	website: ProfileSpecificLink | null | undefined;
	altWebsite: ProfileSpecificLink | null | undefined;
	instagram: ProfileSpecificLink | null | undefined;
	imdb: ProfileSpecificLink | null | undefined;
	x: ProfileSpecificLink | null | undefined;
	tiktok: ProfileSpecificLink | null | undefined;
	linkedin: ProfileSpecificLink | null | undefined;
	facebook: ProfileSpecificLink | null | undefined;
}

export interface UserSearches {
	recentSearchTerms: string[];
	recentlyVisitedTitleIds: string[];
}

export interface DiscoverySettings {
	continueWatchingHintDismissed: boolean;
}

export interface VisibleFlyouts {
	[VisibleFlyoutType.HOME_INVITATION_BOX]: boolean;
	[VisibleFlyoutType.ONBOARDED_HOME]: boolean;
	[VisibleFlyoutType.FREEMIUM_BANNER]: boolean;
	[flyoutName: string]: boolean;
}

export type PiniaStore<T extends (...args: any) => any> = Omit<ReturnType<T>, keyof ReturnType<typeof defineStore>>;

type UserStoreType = PiniaStore<typeof useUserStore>;

export type UserState = UserStoreType['$state'];

export type UserExperimentsInfo = {
	[experimentName: string]: {
		variant: string | null;
		preferred: boolean;
		activated: boolean;
		affiliationShortId: string | null;
	};
};

export const DEFAULT_USERSETTINGS = {
	// fill create map with consent partners valued with `null`
	jw_consents: (Object.keys(CONSENT_PARTNERS) as Consent[]).reduce((root, key) => {
		root[key] = null;
		return root;
	}, {} as Record<Consent, boolean | null>),
	providers: [] as string[],
	hidden_timeline_specials: [] as string[],
	uc_consents: null,
} as UserSettings;
